import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  'w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a365d] focus:border-transparent';

export const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref') || '';

  const [agentInfo, setAgentInfo] = useState(null);
  const [loadingAgent, setLoadingAgent] = useState(true);
  const [invalidRef, setInvalidRef] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [aseguradoras, setAseguradoras] = useState([]);
  const [aseguradoraOtra, setAseguradoraOtra] = useState(false);
  const [birthDate, setBirthDate] = useState('');
  const [rol, setRol] = useState('');

  const ASEGURADORAS_OPTIONS = ['Allianz', 'Insignia Life', 'Quálitas'];

  const toggleAseguradora = (name) => {
    setAseguradoras(prev =>
      prev.includes(name) ? prev.filter(a => a !== name) : [...prev, name]
    );
  };

  useEffect(() => {
    if (!ref) { setInvalidRef(true); setLoadingAgent(false); return; }
    axios.get(`${BACKEND}/api/public/register?ref=${ref}`)
      .then(res => setAgentInfo(res.data))
      .catch(() => setInvalidRef(true))
      .finally(() => setLoadingAgent(false));
  }, [ref]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim()) { setError('El nombre es requerido.'); return; }
    if (!form.last_name.trim()) { setError('El apellido es requerido.'); return; }
    if (!form.email.trim()) { setError('El correo es requerido.'); return; }
    if (aseguradoras.length === 0 && !aseguradoraOtra) {
      setError('Selecciona al menos una aseguradora.'); return;
    }

    const full_name = `${form.first_name.trim()} ${form.last_name.trim()}`;
    const allAseguradoras = aseguradoraOtra ? [...aseguradoras, 'Otra'] : aseguradoras;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/register?ref=${ref}`, {
        full_name,
        email: form.email,
        phone: form.phone || null,
        aseguradoras: allAseguradoras,
        aseguradora_otra: aseguradoraOtra,
        birth_date: birthDate || null,
        rol: rol || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ocurrió un error. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  if (loadingAgent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a365d]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a365d] to-[#2d8659] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/estrapat-logo.png"
            alt="EstraPat"
            className="h-14 object-contain"
            onError={e => { e.target.style.display = 'none'; }}
          />
        </div>

        {invalidRef ? (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Link inválido</h2>
            <p className="text-gray-500">El código de invitación no existe o ha expirado.</p>
          </div>

        ) : submitted ? (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">¡Solicitud enviada!</h2>
            <p className="text-gray-500">
              Gracias, <strong>{form.first_name}</strong>. Tu solicitud fue recibida.
              El equipo de EstraPat revisará tu información y se pondrá en contacto contigo pronto.
            </p>
          </div>

        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-800 text-center mb-1">Únete a EstraPat</h1>
            {agentInfo && (
              <p className="text-center text-sm text-gray-500 mb-6">
                Invitado por{' '}
                <span className="font-medium text-[#1a365d]">{agentInfo.agent_name}</span>
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nombre + Apellido lado a lado */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={set('first_name')}
                    placeholder="Nombre"
                    className={inputClass}
                    required
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apellido <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={set('last_name')}
                    placeholder="Apellido"
                    className={inputClass}
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Correo electrónico <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="correo@ejemplo.com"
                  className={inputClass}
                  required
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="+52 55 1234 5678"
                  className={inputClass}
                  autoComplete="tel"
                />
              </div>

              {/* Aseguradoras */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aseguradoras de interés <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {ASEGURADORAS_OPTIONS.map(name => (
                    <label key={name} className="flex items-center gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={aseguradoras.includes(name)}
                        onChange={() => toggleAseguradora(name)}
                        className="h-4 w-4 rounded border-gray-300 text-[#1a365d] focus:ring-[#1a365d]"
                      />
                      <span className="text-sm text-gray-700">{name}</span>
                    </label>
                  ))}
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={aseguradoraOtra}
                      onChange={e => setAseguradoraOtra(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#1a365d] focus:ring-[#1a365d]"
                    />
                    <span className="text-sm text-gray-700">Otra</span>
                  </label>
                </div>
              </div>

              {/* Fecha de nacimiento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de nacimiento</label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={e => setBirthDate(e.target.value)}
                  className={inputClass}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Rol */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rol de interés</label>
                <div className="flex gap-4">
                  {['Agente', 'Aspirante', 'Asesor'].map(r => (
                    <label key={r} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="rol"
                        value={r}
                        checked={rol === r}
                        onChange={() => setRol(r)}
                        className="h-4 w-4 border-gray-300 text-[#1a365d] focus:ring-[#1a365d]"
                      />
                      <span className="text-sm text-gray-700">{r}</span>
                    </label>
                  ))}
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1a365d] hover:bg-[#1a365d]/90 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {submitting ? 'Enviando...' : 'Enviar solicitud'}
              </button>
            </form>

            <p className="text-center text-xs text-gray-400 mt-4">
              Tu información será revisada por el equipo de EstraPat antes de ser aprobada.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
